// File: src/pages/showingbooker/components/Prospects.js
import React, { useState, useEffect } from 'react';
import { auth } from '../../../firebase';
import { showingbookerAPI } from '../../../services/showingbookerAPI';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  color: #333;
  margin: 0;
  font-size: 24px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  color: #666;
  margin: 5px 0 0 0;
  font-size: 14px;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border: 2px solid ${props => props.active ? '#007AFF' : '#ddd'};
  background: ${props => props.active ? '#007AFF' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 12px;
  }
  
  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f0f0f0'};
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.variant === 'sync' ? '#28a745' : '#007AFF'};
  background: ${props => props.variant === 'sync' ? '#28a745' : '#007AFF'};
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 12px;
  }
  
  &:hover {
    background: ${props => props.variant === 'sync' ? '#218838' : '#0056b3'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
  
  @media (max-width: 768px) {
    min-width: 150px;
    padding: 6px 10px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    min-width: 120px;
    padding: 6px 8px;
    font-size: 12px;
  }
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
`;

const ProspectsTable = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1.5fr 1fr 1fr 120px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  font-size: 14px;
  color: #495057;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    display: none;
  }
`;

const TableHeaderCell = styled.div`
  padding: 15px 20px;
  border-right: 1px solid #e9ecef;
  
  &:last-child {
    border-right: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1.5fr 1fr 1fr 120px;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 1.8fr 0.8fr 1.8fr 1.2fr 0.8fr 0.8fr 100px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 15px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    margin-bottom: 10px;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    
    &:hover {
      background: white;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
  }
`;

const TableCell = styled.div`
  padding: 15px 20px;
  border-right: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    border-right: none;
    padding: 5px 0;
    
    &:before {
      content: attr(data-label);
      font-weight: 600;
      color: #666;
      width: 120px;
      display: inline-block;
      font-size: 12px;
    }
  }
`;

const ContactLink = styled.a`
  color: #007AFF;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #0056b3;
    text-decoration: underline;
  }
`;

const TypeBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => {
    switch (props.type) {
      case 'buyer': return '#28a745';
      case 'buyer_agent': return '#007AFF';
      case 'renter': return '#fd7e14';
      default: return '#6c757d';
    }
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  
  h3 {
    margin: 0 0 10px 0;
    color: #333;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007AFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 10px;
  }
`;

const IconButton = styled.button`
  padding: 6px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.variant === 'delete' ? '#fee' : '#f0f8ff'};
    border-color: ${props => props.variant === 'delete' ? '#dc3545' : '#007AFF'};
    color: ${props => props.variant === 'delete' ? '#dc3545' : '#007AFF'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    color: #333;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  
  &:hover {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.variant === 'primary' ? '#007AFF' : '#ddd'};
  background: ${props => props.variant === 'primary' ? '#007AFF' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background: ${props => props.variant === 'primary' ? '#0056b3' : '#f8f9fa'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Prospects = () => {
  const [user, setUser] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [prospectToDelete, setProspectToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    // Monitor auth state
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      if (user) {
        fetchProspects();
        fetchProperties();
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchProspects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await showingbookerAPI.getProspects();
      setProspects(response.data || []);
    } catch (err) {
      console.error('Error fetching prospects:', err);
      setError('Failed to load prospects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await showingbookerAPI.getProperties();
      setProperties(response || []);
    } catch (err) {
      console.error('Error fetching properties:', err);
      // Don't show error for properties since it's not critical
    }
  };

  const syncProspects = async () => {
    try {
      setSyncing(true);
      setError(null);
      const response = await showingbookerAPI.syncProspects();
      console.log('Sync response:', response);
      // Refresh the prospects list after sync
      await fetchProspects();
    } catch (err) {
      console.error('Error syncing prospects:', err);
      setError('Failed to sync prospects. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleEditProspect = (prospect) => {
    // Format booking_date for input field (YYYY-MM-DD format)
    let formattedBookingDate = '';
    if (prospect.booking_date) {
      const date = new Date(prospect.booking_date);
      if (!isNaN(date.getTime())) {
        formattedBookingDate = date.toISOString().split('T')[0];
      }
    }

    setEditingProspect({
      id: prospect.id,
      first_name: prospect.first_name || '',
      last_name: prospect.last_name || '',
      email: prospect.email || '',
      phone: prospect.phone || '',
      prospect_type: prospect.prospect_type || 'buyer',
      status: prospect.status || 'active',
      property_id: prospect.property_id || '',
      booking_date: formattedBookingDate
    });
    setShowEditModal(true);
  };

  const handleDeleteProspect = (prospect) => {
    setProspectToDelete(prospect);
    setShowDeleteModal(true);
  };

  const saveProspect = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      
      await showingbookerAPI.updateProspect(editingProspect.id, editingProspect);
      
      // Refresh the prospects list
      await fetchProspects();
      
      // Close modal
      setShowEditModal(false);
      setEditingProspect(null);
    } catch (err) {
      console.error('Error updating prospect:', err);
      setError('Failed to update prospect. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await showingbookerAPI.deleteProspect(prospectToDelete.id);
      
      // Refresh the prospects list
      await fetchProspects();
      
      // Close modal
      setShowDeleteModal(false);
      setProspectToDelete(null);
    } catch (err) {
      console.error('Error deleting prospect:', err);
      setError('Failed to delete prospect. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setEditingProspect(null);
    setProspectToDelete(null);
  };

  const getFilteredProspects = () => {
    let filtered = prospects;

    // Filter by type
    if (filter !== 'all') {
      filtered = filtered.filter(prospect => prospect.prospect_type === filter);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(prospect =>
        `${prospect.first_name} ${prospect.last_name}`.toLowerCase().includes(term) ||
        prospect.email?.toLowerCase().includes(term) ||
        prospect.phone?.includes(term) ||
        prospect.property_address?.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTypeLabel = (type) => {
    switch (type) {
      case 'buyer': return 'Buyer';
      case 'buyer_agent': return "Buyer's Agent";
      case 'renter': return 'Prospective Renter';
      default: return type;
    }
  };

  const formatPropertyLabel = (property) => {
    if (!property) return '';
    const address = property.address_line1;
    const unit = property.unit_number ? ` Unit ${property.unit_number}` : '';
    const city = property.city;
    const state = property.state;
    return `${address}${unit}, ${city}, ${state}`;
  };

  const filteredProspects = getFilteredProspects();

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <LoadingSpinner />
          <p style={{ marginTop: '15px', color: '#666' }}>Loading prospects...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>Prospects</Title>
          <Subtitle>All leads from your booking system consolidated in one place</Subtitle>
        </div>
        
        <FilterContainer>
          <SearchInput
            type="text"
            placeholder="Search prospects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <ActionButton
            onClick={fetchProspects}
            disabled={loading}
            title="Refresh prospects list"
          >
            🔄 Refresh
          </ActionButton>
          
          <ActionButton
            variant="sync"
            onClick={syncProspects}
            disabled={syncing || loading}
            title="Sync prospects from recent bookings"
          >
            {syncing ? '⏳ Syncing...' : '🔄 Sync from Bookings'}
          </ActionButton>
          
          <FilterButton
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          >
            All ({prospects.length})
          </FilterButton>
          
          <FilterButton
            active={filter === 'buyer'}
            onClick={() => setFilter('buyer')}
          >
            Buyers ({prospects.filter(p => p.prospect_type === 'buyer').length})
          </FilterButton>
          
          <FilterButton
            active={filter === 'buyer_agent'}
            onClick={() => setFilter('buyer_agent')}
          >
            Agents ({prospects.filter(p => p.prospect_type === 'buyer_agent').length})
          </FilterButton>
          
          <FilterButton
            active={filter === 'renter'}
            onClick={() => setFilter('renter')}
          >
            Renters ({prospects.filter(p => p.prospect_type === 'renter').length})
          </FilterButton>
        </FilterContainer>
      </Header>

      {error && (
        <div style={{ 
          background: '#f8d7da', 
          color: '#721c24', 
          padding: '15px', 
          borderRadius: '8px', 
          marginBottom: '20px' 
        }}>
          {error}
        </div>
      )}

      <ProspectsTable>
        <TableHeader>
          <TableHeaderCell>Name</TableHeaderCell>
          <TableHeaderCell>Type</TableHeaderCell>
          <TableHeaderCell>Contact</TableHeaderCell>
          <TableHeaderCell>Property</TableHeaderCell>
          <TableHeaderCell>Booking Date</TableHeaderCell>
          <TableHeaderCell>Status</TableHeaderCell>
          <TableHeaderCell>Actions</TableHeaderCell>
        </TableHeader>

        {filteredProspects.length === 0 ? (
          <EmptyState>
            <h3>No prospects found</h3>
            <p>
              {searchTerm || filter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Prospects will appear here as people book showings through your property pages.'
              }
            </p>
          </EmptyState>
        ) : (
          filteredProspects.map((prospect) => (
            <TableRow key={prospect.id}>
              <TableCell data-label="Name:">
                <strong>{prospect.first_name} {prospect.last_name}</strong>
              </TableCell>
              
              <TableCell data-label="Type:">
                <TypeBadge type={prospect.prospect_type}>
                  {formatTypeLabel(prospect.prospect_type)}
                </TypeBadge>
              </TableCell>
              
              <TableCell data-label="Contact:">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {prospect.email && (
                    <ContactLink href={`mailto:${prospect.email}`}>
                      {prospect.email}
                    </ContactLink>
                  )}
                  {prospect.phone && (
                    <ContactLink href={`tel:${prospect.phone.replace(/\D/g, '')}`}>
                      {prospect.phone}
                    </ContactLink>
                  )}
                </div>
              </TableCell>
              
              <TableCell data-label="Property:">
                {prospect.property_address || 'N/A'}
              </TableCell>
              
              <TableCell data-label="Booking Date:">
                {formatDate(prospect.booking_date)}
              </TableCell>
              
              <TableCell data-label="Status:">
                <span style={{ 
                  color: prospect.status === 'active' ? '#28a745' : '#6c757d',
                  fontWeight: '500',
                  textTransform: 'capitalize'
                }}>
                  {prospect.status || 'Active'}
                </span>
              </TableCell>
              
              <TableCell data-label="Actions:">
                <ActionButtons>
                  <IconButton
                    onClick={() => handleEditProspect(prospect)}
                    title="Edit prospect"
                  >
                    ✏️
                  </IconButton>
                  <IconButton
                    variant="delete"
                    onClick={() => handleDeleteProspect(prospect)}
                    title="Delete prospect"
                  >
                    🗑️
                  </IconButton>
                </ActionButtons>
              </TableCell>
            </TableRow>
          ))
        )}
      </ProspectsTable>

      {/* Edit Modal */}
      {showEditModal && editingProspect && (
        <Modal onClick={closeModals}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Edit Prospect</h3>
              <CloseButton onClick={closeModals}>×</CloseButton>
            </ModalHeader>
            
            <Form onSubmit={saveProspect}>
              <FormGroup>
                <Label>First Name</Label>
                <Input
                  type="text"
                  value={editingProspect.first_name}
                  onChange={(e) => setEditingProspect({
                    ...editingProspect,
                    first_name: e.target.value
                  })}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  value={editingProspect.last_name}
                  onChange={(e) => setEditingProspect({
                    ...editingProspect,
                    last_name: e.target.value
                  })}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editingProspect.email}
                  onChange={(e) => setEditingProspect({
                    ...editingProspect,
                    email: e.target.value
                  })}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={editingProspect.phone}
                  onChange={(e) => setEditingProspect({
                    ...editingProspect,
                    phone: e.target.value
                  })}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Type</Label>
                <Select
                  value={editingProspect.prospect_type}
                  onChange={(e) => setEditingProspect({
                    ...editingProspect,
                    prospect_type: e.target.value
                  })}
                >
                  <option value="buyer">Buyer</option>
                  <option value="buyer_agent">Buyer's Agent</option>
                  <option value="renter">Prospective Renter</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Status</Label>
                <Select
                  value={editingProspect.status}
                  onChange={(e) => setEditingProspect({
                    ...editingProspect,
                    status: e.target.value
                  })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="converted">Converted</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Property</Label>
                <Select
                  value={editingProspect.property_id}
                  onChange={(e) => setEditingProspect({
                    ...editingProspect,
                    property_id: e.target.value
                  })}
                >
                  <option value="">No property assigned</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {formatPropertyLabel(property)}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Booking Date</Label>
                <Input
                  type="date"
                  value={editingProspect.booking_date}
                  onChange={(e) => setEditingProspect({
                    ...editingProspect,
                    booking_date: e.target.value
                  })}
                />
              </FormGroup>
              
              <ButtonGroup>
                <Button type="button" onClick={closeModals}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && prospectToDelete && (
        <Modal onClick={closeModals}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Delete Prospect</h3>
              <CloseButton onClick={closeModals}>×</CloseButton>
            </ModalHeader>
            
            <p>
              Are you sure you want to delete <strong>{prospectToDelete.first_name} {prospectToDelete.last_name}</strong>?
              This action cannot be undone.
            </p>
            
            <ButtonGroup>
              <Button type="button" onClick={closeModals}>Cancel</Button>
              <Button 
                type="button" 
                variant="primary" 
                onClick={confirmDelete}
                disabled={saving}
                style={{ background: '#dc3545', borderColor: '#dc3545' }}
              >
                {saving ? 'Deleting...' : 'Delete Prospect'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Prospects;